/**
 * The players this viewer follows, out of the ones a card might name.
 *
 * The class block under a module's playlist wants to lead with a name the reader
 * recognises — *Sami played this module* rather than *eleven players so far* — and
 * the one thing that makes a name recognisable is that the reader chose it: a did
 * on their own follow list. Nothing on the board says that. Follow state is a
 * *relationship*, and it lives on a profile (`ProfileView.viewer.following`), so
 * it has to be asked for separately, by profile, after the board has said which
 * profiles are worth asking about.
 *
 * ## Why the caller passes the dids rather than the hook reading the board
 *
 * The callers do not all have a board — the module's own screen and the card in a
 * feed assemble the audience slightly differently — and the list of names the
 * sentence *can* say is a different question from who played: it is the leading
 * rows, which is exactly the cap this hook is handed (`MODULE_PRIZE_WINNERS` at
 * the widest). Keeping the choice with the caller also keeps the request bounded:
 * a hundred-row board asks about ten rows, not a hundred.
 *
 * ## Why it is allowed to ask nothing at all
 *
 * A feed draws a module card per scroll, and a hook that fired a request per card
 * whether or not anybody would read the sentence would put a network cost on a
 * timeline for copy that most cards never print. So the caller passes an empty
 * list when the answer cannot matter (nobody has played, the reader is not a
 * peer), and a signed-out reader is filtered out here rather than at five call
 * sites. An unanswered profile is *not* a friend: the set simply does not contain
 * it, and the card falls back to the count.
 *
 * One request per distinct list of dids, cached by react-query like every other
 * profile lookup in the app (`#/state/queries/profile`), so scrolling past the
 * same module twice costs one call.
 */
import {useMemo} from 'react'

import {useProfilesQuery} from '#/state/queries/profile'
import {useSession} from '#/state/session'

/**
 * Which of `dids` the current account follows.
 *
 * Returns a set of *dids*, because that is how the audience lib asks the question
 * (`following.has(row.did)`) and building it once here is what keeps the rule out
 * of the render path. Empty means nobody — never *unknown*, which callers treat
 * identically on purpose.
 */
export function useModuleFollowing(
  dids: readonly string[],
): ReadonlySet<string> {
  const {currentAccount} = useSession()

  /*
   * The list is a *request key*, so it is stabilised here rather than in the
   * caller: an inline array of the same dids would be a new array every render and
   * a new react-query key with it, which is one request per render.
   */
  const actors = useMemo(() => [...new Set(dids)].filter(did => !!did), [dids])

  const {data} = useProfilesQuery({
    handles: currentAccount ? actors : [],
  })

  return useMemo(() => {
    const following = new Set<string>()
    for (const profile of data?.profiles ?? []) {
      if (profile.viewer?.following) following.add(profile.did)
    }
    return following
  }, [data])
}
